/**
 * Four-view detail modal — the substance behind each headline, computed from the selected
 * scenario, the same source as the card (build handover §6). Opens over the centre; closing
 * returns to it.
 */

import {
  atRiskValue,
  channelShare,
  monthlyTradeSales,
  MONTHS,
  type ScenarioContext,
  type ScenarioResult,
} from "@/lib/data";

import { ACCOUNTS, PLANTS, ROUTES, ROUTES_FULL_RELOC_MOVE_PCT, SECOND_SITE } from "./mock";
import type { SavedScenario, ViewModal } from "./state";

const fmt = (n: number) => Math.round(n).toLocaleString("en-US");

export function FourViewModal({
  vmodal,
  scenarios,
  viewSel,
  sm,
  ctx,
  onClose,
}: {
  vmodal: ViewModal;
  scenarios: SavedScenario[];
  viewSel: number;
  sm: ScenarioResult | null;
  ctx: ScenarioContext;
  onClose: () => void;
}) {
  if (!vmodal) {
    return null;
  }
  const caseName = viewSel >= 0 ? (scenarios[viewSel]?.name ?? "Current state") : "Current state";

  return (
    <div className="vmodal on">
      <div className="vmodal-back" onClick={onClose} />
      <div className="vmodal-card">
        <div className="vmodal-head">
          <div className="t">{titleFor(vmodal)}</div>
          <div className="s">
            {questionFor(vmodal)} · {caseName}
          </div>
          <button className="drawer-close" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="vmodal-body">
          {vmodal === "business" ? <BusinessDetail sm={sm} ctx={ctx} /> : null}
          {vmodal === "customers" ? <CustomersDetail sm={sm} /> : null}
          {vmodal === "channels" ? <ChannelsDetail sm={sm} ctx={ctx} /> : null}
          {vmodal === "operations" ? <OperationsDetail sm={sm} ctx={ctx} /> : null}
        </div>
      </div>
    </div>
  );
}

function titleFor(m: NonNullable<ViewModal>) {
  return {
    business: "Business",
    customers: "Customers",
    channels: "Channels",
    operations: "Operations",
  }[m];
}
function questionFor(m: NonNullable<ViewModal>) {
  return {
    business: "what does it cost you?",
    customers: "who feels it?",
    channels: "what changes route?",
    operations: "what do you get back?",
  }[m];
}

function BusinessDetail({ sm, ctx }: { sm: ScenarioResult | null; ctx: ScenarioContext }) {
  const atRisk = sm ? atRiskValue(sm.claim, ctx.skus) : 0;
  const { total, atRisk: byMonth } = monthlyTradeSales(ctx.skus, sm ? sm.claim : null);
  const ntsM = ctx.site.nts / 1_000_000;
  return (
    <>
      <div className="vm-sec">Trade sales by month</div>
      <table className="ptable">
        <thead>
          <tr>
            <th>Month</th>
            <th className="num">Trade sales</th>
            {atRisk > 0 ? <th className="num">At risk</th> : null}
          </tr>
        </thead>
        <tbody>
          {total.map((v, i) => (
            <tr key={i}>
              <td>{MONTHS[i]}</td>
              <td className="num">£{(v / 1_000_000).toFixed(2)}m</td>
              {atRisk > 0 ? (
                <td className="num" style={{ color: "var(--jj-red-ink)" }}>
                  − £{((byMonth[i] ?? 0) / 1_000_000).toFixed(2)}m
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="vm-note">
        Twelve months carried: <b>£{ntsM.toFixed(0)}m</b>
        {atRisk > 0 ? (
          <>
            {" "}
            · at risk under this case: <b>£{atRisk.toFixed(1)}m</b>, off the real SKUs the levers
            claimed.
          </>
        ) : null}
      </div>
    </>
  );
}

function CustomersDetail({ sm }: { sm: ScenarioResult | null }) {
  return (
    <>
      <div className="vm-sec">Top accounts by trade value</div>
      <table className="ptable">
        <thead>
          <tr>
            <th>Account</th>
            <th>Tier</th>
            <th>Country</th>
            <th className="num">£m / month</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {ACCOUNTS.map((a) => {
            let status = "—";
            if (sm) {
              if (a.waiver && sm.waiverCount > 0) {
                status = "waiver needed";
              } else if (a.slow) {
                status = "affected";
              }
            }
            return (
              <tr key={a.name}>
                <td>{a.name}</td>
                <td>{a.tier}</td>
                <td>{a.country}</td>
                <td className="num">£{a.monthlyM.toFixed(2)}m</td>
                <td>{status}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="vm-note">
        Named accounts are illustrative (mock.ts) — showing the top {ACCOUNTS.length} of a real
        base.
        {sm
          ? " Status here follows the real waiver/affected flags this case sets."
          : " Nothing proposed touches an account in this case."}
      </div>
    </>
  );
}

function ChannelsDetail({ sm, ctx }: { sm: ScenarioResult | null; ctx: ScenarioContext }) {
  const moved = sm ? channelShare(sm.claim, ctx.skus) : 0;
  return (
    <>
      <div className="vm-sec">Route breakdown</div>
      <table className="ptable">
        <thead>
          <tr>
            <th>Route</th>
            <th className="num">Share</th>
            <th>Served from</th>
            <th>Change</th>
          </tr>
        </thead>
        <tbody>
          {ROUTES.map((r) => {
            const mv = moved > 0 ? r.movesAtFull * (moved / ROUTES_FULL_RELOC_MOVE_PCT) : 0;
            const served =
              mv > 0
                ? `${(100 - (mv / r.share) * 100).toFixed(0)}% UKDC · ${((mv / r.share) * 100).toFixed(0)}% ${SECOND_SITE.name}`
                : "100% UKDC";
            const change =
              moved > 0 && r.opensAtFull
                ? "route opens"
                : mv > 0
                  ? `− ${mv.toFixed(1)}pp to ${SECOND_SITE.name}`
                  : "—";
            return (
              <tr key={r.name}>
                <td>{r.name}</td>
                <td className="num">{r.share}%</td>
                <td>{served}</td>
                <td>{change}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="vm-note">
        Product flows {PLANTS.map((p) => `${p.name} (${p.share}%)`).join(" and ")} into UKDC, then
        out through {ROUTES.length} routes.{" "}
        {moved > 0 ? (
          <>
            Under this case <b>{moved.toFixed(1)}%</b> of trade value crosses to {SECOND_SITE.name}.
          </>
        ) : (
          "Every route is served from UKDC in this case."
        )}
      </div>
    </>
  );
}

function OperationsDetail({ sm, ctx }: { sm: ScenarioResult | null; ctx: ScenarioContext }) {
  const years = ctx.model.years?.length ? ctx.model.years : [2026, 2027, 2028, 2029];
  const active = sm;
  return (
    <>
      <div className="vm-sec">What the levers change</div>
      <table className="ptable">
        <thead>
          <tr>
            <th>Lever</th>
            <th className="num">Pick faces</th>
          </tr>
        </thead>
        <tbody>
          {active ? (
            <>
              <tr>
                <td>Relocate non-movers</td>
                <td className="num">{active.relocate > 0 ? `− ${fmt(active.relocate)}` : "—"}</td>
              </tr>
              <tr>
                <td>Pack rationalize</td>
                <td className="num">{active.pack > 0 ? `− ${fmt(active.pack)}` : "—"}</td>
              </tr>
              <tr>
                <td>Rx to Dx convert</td>
                <td className="num">{active.conv > 0 ? `− ${fmt(active.conv)}` : "—"}</td>
              </tr>
              <tr>
                <td>Added capacity</td>
                <td className="num">{active.addFaces > 0 ? `+ ${fmt(active.addFaces)}` : "—"}</td>
              </tr>
            </>
          ) : (
            <tr>
              <td colSpan={2} style={{ color: "var(--muted)" }}>
                No levers in this case, the position as planned.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <div className="vm-sec">Held vs ceiling by year</div>
      <table className="ptable">
        <thead>
          <tr>
            <th>Year</th>
            <th className="num">Held</th>
            <th className="num">Ceiling</th>
            <th className="num">Position</th>
            <th className="num">%</th>
          </tr>
        </thead>
        <tbody>
          {years.map((y) => {
            const held = active ? (active.held[y] ?? 0) : 0;
            const ceil = active ? active.ceiling : ctx.model.capd.ceil;
            const over = held > ceil;
            return (
              <tr key={y}>
                <td>{y}</td>
                <td className="num">{fmt(held)}</td>
                <td className="num">{fmt(ceil)}</td>
                <td className="num" style={{ color: over ? "var(--jj-red-ink)" : "var(--ok)" }}>
                  {over ? `over by ${fmt(held - ceil)}` : `room + ${fmt(ceil - held)}`}
                </td>
                <td className="num">{ceil > 0 ? ((held / ceil) * 100).toFixed(1) : "0.0"}%</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}
